import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputLabel,
    Stack,
    Typography,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { Controller, useFormContext } from 'react-hook-form-v7';
import moment from 'moment';
import { COLORS } from '../../../utils/Application_Constants';
import ConnectedDatePicker from '../ProcessedTimesheets/ConnectedDatePicker';
import Switch from '../../../Shared/Components/Switch';

// Helper function to determine minimum allowed date
// During first 15 days of the year (Jan 1-15), allow 12/31 of previous year
// After first 15 days, minimum is current date
const getMinimumAllowedDate = () => {
    const currentDate = moment().startOf('day');
    const currentMonth = currentDate.month(); // 0-indexed (0 = January)
    const currentDay = currentDate.date();

    if (currentMonth === 0 && currentDay <= 15) {
        return moment().subtract(1, 'year').endOf('year').startOf('day');
    }
    return currentDate;
};

const validateInvoiceDateRange = (selectedDate, label) => {
    if (!selectedDate) return `${label} is required.`;

    const minDate = getMinimumAllowedDate();
    const maxDate = moment().add(15, 'days').endOf('day');

    if (!selectedDate.isBetween(minDate, maxDate, 'day', '[]')) {
        if (selectedDate.isBefore(minDate, 'day')) {
            return `${label} cannot be before ${minDate.format('MM/DD/YYYY')}.`;
        }
        return `${label} cannot be more than 15 days in the future.`;
    }

    return true;
};

const styles = () => ({
    datePicker: {
        '& .MuiPickersSectionList-root': {
            padding: '13px 0',
            fontWeight: 'normal',
            fontSize: 14,
        },
        '& .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: COLORS.LT_MIDNIGHT10,
        },
        '& .MuiFormHelperText-root': {
            marginLeft: 0,
            position: 'absolute',
            top: '100%',
        },
        '&.MuiPickersTextField-root': {
            marginBottom: 0,
        },
    },
    formLabel: {
        marginTop: 12,
        '& .MuiTypography-root': {
            fontSize: 14,
        },
    },
});

const InvoiceConfirmationModal = ({
    classes,
    open,
    onClose,
    title,
    showInvoiceForm,
    bodyMessage,
    onSubmit,
    isLoading,
    error,
    actionButtonLabel,
    actionButtonColor = 'primary',
}) => {
    const { control } = useFormContext();

    return (
        <Dialog
            open={open}
            onClose={isLoading ? null : onClose}
            maxWidth="sm"
            slotProps={{ paper: { sx: { borderRadius: '8px', padding: '30px' } } }}
        >
            <DialogTitle>
                <Typography variant="heading3" sx={{ color: COLORS.BLACK }}>
                    {title}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <form onSubmit={onSubmit}>
                    <Typography variant="body2">{bodyMessage}</Typography>
                    {showInvoiceForm && (
                        <>
                            <Stack
                                sx={{
                                    border: `1px solid ${COLORS.LT_MIDNIGHT10} `,
                                    mt: 2,
                                    p: 1,
                                    borderRadius: '8px',
                                }}
                                gap={2}
                            >
                                <Typography
                                    variant="heading5"
                                    sx={{ fontWeight: 'bold', color: COLORS.BLACK }}
                                >
                                    Verify Dates
                                </Typography>
                                <Typography variant="body2">
                                    The Posting Date affects your accounting records, and the
                                    Invoice Date is the official client issue date. Please confirm.
                                </Typography>
                                <Stack direction="row" gap={2}>
                                    <Box sx={{ mr: 2 }}>
                                        <InputLabel
                                            sx={{ fontSize: 14, mb: 1 }}
                                            id="rate-type-label"
                                        >
                                            Posting Date
                                        </InputLabel>
                                        <ConnectedDatePicker
                                            control={control}
                                            name="postingDate"
                                            pickerProps={{
                                                className: classes.datePicker,
                                                format: 'MM/DD/YYYY',
                                                minDate: getMinimumAllowedDate(),
                                                maxDate: moment().add(15, 'days').endOf('day'),
                                            }}
                                            rules={{
                                                required: 'Posting Date is required.',
                                                validate: (selectedDate) =>
                                                    validateInvoiceDateRange(
                                                        selectedDate,
                                                        'Posting Date'
                                                    ),
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ mr: 2 }}>
                                        <InputLabel
                                            sx={{ fontSize: 14, mb: 1 }}
                                            id="rate-type-label"
                                        >
                                            Invoice Date
                                        </InputLabel>
                                        <ConnectedDatePicker
                                            control={control}
                                            name="invoiceDate"
                                            pickerProps={{
                                                className: classes.datePicker,
                                                format: 'MM/DD/YYYY',
                                                minDate: getMinimumAllowedDate(),
                                                maxDate: moment().add(15, 'days').endOf('day'),
                                            }}
                                            rules={{
                                                required: 'Invoice Date is required.',
                                                validate: (selectedDate) =>
                                                    validateInvoiceDateRange(
                                                        selectedDate,
                                                        'Invoice Date'
                                                    ),
                                            }}
                                        />
                                    </Box>
                                </Stack>
                            </Stack>
                            <Controller
                                control={control}
                                name="doNotSendToClient"
                                render={({ field }) => (
                                    <FormControlLabel
                                        name="doNotSendToClient"
                                        className={classes.formLabel}
                                        control={
                                            <Switch
                                                checked={field.value}
                                                onChange={field.onChange}
                                            />
                                        }
                                        label="Do not send to client"
                                    />
                                )}
                            />
                        </>
                    )}
                    <Stack direction="row" sx={{ mt: 4 }}>
                        <Button
                            variant="lt-admin-outlined"
                            color="primary"
                            sx={{ mr: 2 }}
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="lt-admin-contained"
                            color={actionButtonColor}
                            type="submit"
                            loading={isLoading}
                            sx={{ ml: 'auto' }}
                        >
                            {actionButtonLabel}
                        </Button>
                    </Stack>
                    {error?.message && (
                        <Stack sx={{ mt: 2 }}>
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                {error.message}
                            </Typography>
                            {error?.errors && error?.errors?.length > 0 && (
                                <ul style={{ marginTop: 0, paddingLeft: '20px' }}>
                                    {error.errors.map((lineError, index) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <li key={index} style={{ color: COLORS.MILANO_RED }}>
                                            <Typography
                                                variant="body2"
                                                color="error"
                                                sx={{ fontSize: 12 }}
                                            >
                                                {lineError}
                                            </Typography>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Stack>
                    )}
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default withStyles(styles)(InvoiceConfirmationModal);
